import React, { useState, useEffect } from 'react';
import Menu from './Menu';
import Header from './Header';

/**
 * User layout with vertical navigation sidebar and content area
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.currentPage - Current active page
 * @param {Function} props.onNavigate - Navigation handler
 * @param {Function} props.onLogout - Logout handler
 */
const UserLayout = ({ children, currentPage, onNavigate, onLogout }) => {
    // Full-screen pages (no padding, no header)
    const isFullScreenPage = currentPage === 'map';

    // Mobile sidebar state - closed by default on mobile
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Toggle mobile menu
    const toggleMobileMenu = () => {
        // Only toggle on mobile (sm breakpoint = 640px)
        if (window.innerWidth < 640) {
            setIsMobileMenuOpen(!isMobileMenuOpen);
        }
    };

    // Close mobile menu when navigating
    const handleNavigate = (page) => {
        setIsMobileMenuOpen(false);
        onNavigate(page);
    };

    // Close menu when resizing to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 640) {
                setIsMobileMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className="flex h-screen bg-white">
            {/* Mobile overlay */}
            {isMobileMenuOpen && (
                <div
                    className="sm:hidden fixed inset-0 bg-black/50 z-[9998]"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Vertical Sidebar - Hidden on mobile, overlay when open */}
            <div className={`
                fixed sm:relative z-[9999]
                transform transition-transform duration-300 ease-in-out
                ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
            `}>
                <Menu
                    currentPage={currentPage}
                    onNavigate={handleNavigate}
                    onLogout={onLogout}
                />
            </div>

            {/* Main Content Area */}
            {isFullScreenPage ? (
                <div className="flex-1 overflow-hidden flex flex-col">
                    {/* Mobile header for fullscreen pages */}
                    <div className="sm:hidden flex-shrink-0 bg-white border-b border-gray-200 px-3 py-2">
                        <Header
                            onToggleMenu={toggleMobileMenu}
                            showMenuButton={!isMobileMenuOpen}
                        />
                    </div>
                    {/* Fullscreen content */}
                    <div className="flex-1 overflow-hidden">
                        {children}
                    </div>
                </div>
            ) : (
                <div className="flex-1 overflow-auto bg-white">
                    <div className="max-w-7xl mx-auto p-4 sm:p-6">
                        {/* Header with hamburger on mobile */}
                        <Header
                            onToggleMenu={toggleMobileMenu}
                            showMenuButton={!isMobileMenuOpen}
                        />

                        {/* Page Content */}
                        {children}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserLayout;
